/**
 * Use it when you want debounce function e.g. input typing.
 * Example:
 * ```ts
   <tamplate>
      <input type="text" @input="typing" />
   </template>
   <script lang="ts" setup>
      import { debounce } from "design-system-ui-components"

      const typing = debounce((e: Event) => {
         const inputValue = (e.target as HTMLInputElement).value

         emit("input", inputValue)
      }, 500)
   </script>
 * ```
 * @param fn - Function to debounce
 * @param ms - Delay in milliseconds
 */
export function debounce<A = unknown, R = void>(
   fn: (args: A) => R,
   ms: number
): (args: A) => Promise<R> {
   let timer: NodeJS.Timeout

   const debouncedFunc = (args: A): Promise<R> =>
      new Promise((resolve) => {
         if (timer) {
            clearTimeout(timer)
         }

         timer = setTimeout(() => {
            resolve(fn(args))
         }, ms)
      })

   return debouncedFunc
}
